// BestRx — Medication detail page (white aesthetic, conversion-first, 8 sections).
import { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowRight, Phone, ShieldCheck } from "lucide-react";
import Footer from "@/components/landing/Footer";
import { useLeadModal } from "@/components/shared/LeadProvider";
import { MEDICATIONS, ALL_MEDS } from "@/data/medications";
import { PRICING, WHO_FOR, fallbackPricing } from "@/data/medPricing";
import { CITIES } from "@/data/cities";
import SEO, { drugSchema, breadcrumbSchema } from "@/components/shared/SEO";
import {
  PricingCards,
  NoInsuranceSection,
  HowItWorks4,
  RequestCallbackCTA,
  WhyBestRx,
  WhoIsFor,
  FAQAccordion,
  RelatedCluster,
  faqSchemaFrom,
  RED,
} from "@/components/shared/BestRxSections";
import {
  WhatIsIt,
  WhyPatientsChoose,
  ExpectedResults,
  AvailableStrengths,
  CategoryLinkback,
} from "@/components/shared/MedicationSections";

const PHONE = "844-703-1780";
const TEL = "tel:+18447031780";

function HeaderBar({ onCta }) {
  return (
    <header className="absolute top-0 left-0 right-0 z-40 bg-white border-b border-black/10">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 py-4 flex items-center justify-between gap-4">
        <a href="/" className="font-heavy text-black leading-none" style={{ fontSize: "clamp(24px, 2.6vw, 36px)" }} data-testid="brand-logo">
          BEST<span style={{ color: RED }}>RX</span>
        </a>
        <div className="flex items-center gap-3">
          <a href={TEL} className="hidden sm:inline-flex items-center gap-2 font-extrabold text-[13px]" style={{ color: RED }} data-testid="header-phone">
            <Phone size={16} /> {PHONE}
          </a>
          <button
            onClick={onCta}
            data-testid="header-cta"
            className="inline-flex items-center gap-2 px-4 sm:px-6 py-3 font-extrabold uppercase text-[11.5px] sm:text-[13px] text-white tracking-[0.06em]"
            style={{ background: RED, boxShadow: "0 8px 20px rgba(225,29,42,0.30)" }}
          >
            Start Now <ArrowRight size={16} strokeWidth={2.6} />
          </button>
        </div>
      </div>
    </header>
  );
}

export default function MedicationPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const m = MEDICATIONS[slug];
  const { open } = useLeadModal();
  const onCta = () => open({ source: `med_${slug}` });
  // Strength card → opens qualifier with the selected dose attached as source.
  const onPickStrength = (dose, unit) => open({ source: `med_${slug}_${dose}${unit}` });

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!m) {
    return (
      <main className="bg-white min-h-screen text-black">
        <SEO title="Not Found" path={`/medications/${slug}`} noindex />
        <HeaderBar onCta={onCta} />
        <div className="pt-40 text-center px-6">
          <h1 className="font-heavy text-5xl">Not found</h1>
          <button onClick={() => navigate("/")} className="mt-8 px-6 py-3 font-extrabold text-white" style={{ background: RED }}>Back home →</button>
        </div>
        <Footer />
      </main>
    );
  }

  const packages = PRICING[slug] || fallbackPricing(slug, 199);
  const startingPrice = packages[0]?.price || 199;
  const whoFor = WHO_FOR[slug] || [];
  const path = `/medications/${slug}`;
  const description = `${m.name} in Texas via BestRx — no insurance required, new customers start as low as $35 week 1*. Starting at $${startingPrice}/month. Provider-reviewed, pharmacy-compounded, delivered to your door.`;

  return (
    <main className="bg-white text-black overflow-x-hidden min-h-screen" data-testid={`med-page-${slug}`}>
      <SEO
        title={`${m.name} · From $${startingPrice}/mo · No Insurance Required`}
        path={path}
        description={description}
        schema={[
          drugSchema({ name: m.name, summary: m.tagline }),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: m.category, path: `/category/${(m.category || "").toLowerCase().replace(/\s+/g, "-")}` },
            { name: m.name, path },
          ]),
          faqSchemaFrom(m.faq || []),
        ]}
      />
      <HeaderBar onCta={onCta} />

      {/* SECTION 1 — HERO */}
      <section className="relative pt-28 sm:pt-32 md:pt-36 pb-12 md:pb-20" data-testid="med-hero">
        <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="font-extrabold tracking-[0.18em] mb-3 text-[12px] md:text-[13px]" style={{ color: RED }}>
              {m.category.toUpperCase()} · {m.type?.toUpperCase()}
            </p>
            <h1 className="font-heavy leading-[0.92]" style={{ fontSize: "clamp(40px, 6.2vw, 104px)" }} data-testid="med-h1">
              <span className="text-black">{m.name}</span>
            </h1>
            <p className="mt-5 text-[16px] md:text-[19px] text-black/75 leading-relaxed max-w-[640px]">
              {m.tagline}
            </p>

            {/* Benefits chips */}
            <div className="mt-6 flex flex-wrap gap-2">
              {(m.bullets || []).map((b) => (
                <span key={b} className="inline-flex items-center gap-1.5 px-3 py-1.5 border-2 border-black text-[11.5px] font-extrabold tracking-[0.06em] uppercase">
                  <ShieldCheck size={14} color={RED} strokeWidth={2.6} /> {b}
                </span>
              ))}
            </div>

            {/* Price + CTAs */}
            <div
              className="mt-8 rounded-md border-2 p-6 md:p-7 inline-flex flex-col sm:flex-row items-start sm:items-center gap-5"
              style={{ borderColor: RED, boxShadow: "0 0 0 6px rgba(225,29,42,0.06)" }}
              data-testid="med-price-block"
            >
              <div>
                <p className="font-extrabold text-[11px] tracking-[0.18em]" style={{ color: RED }}>
                  STARTING AS LOW AS
                </p>
                <p className="font-heavy leading-none mt-1" style={{ fontSize: "clamp(40px, 4.6vw, 64px)", color: RED }}>
                  ${startingPrice}<span className="text-black/60 font-bold text-[18px] md:text-[22px] ml-1">/ month</span>
                </p>
                <p className="font-extrabold text-[12.5px] tracking-[0.12em] mt-2 text-black uppercase">
                  No Insurance Required · <span style={{ color: RED }}>$35 Week 1<sup>*</sup></span>
                </p>
              </div>
              <div className="flex flex-col gap-3 sm:ml-auto">
                <button
                  onClick={onCta}
                  data-testid="med-cta-start"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 font-extrabold text-white tracking-[0.08em] uppercase text-[13px]"
                  style={{ background: RED, boxShadow: "0 12px 28px rgba(225,29,42,0.30)" }}
                >
                  Start Now <ArrowRight size={16} strokeWidth={2.6} />
                </button>
                <a
                  href={TEL}
                  data-testid="med-cta-call"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 font-extrabold tracking-[0.08em] uppercase text-[13px] border-2 border-black text-black"
                >
                  <Phone size={16} /> {PHONE}
                </a>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 flex items-center justify-center">
            <img
              src="/assets/hero-meds.png?v=2"
              alt={`${m.name} provider-prescribed via BestRx`}
              className="w-full h-auto max-h-[520px] object-contain"
              draggable={false}
              data-testid="med-hero-image"
            />
          </div>
        </div>
      </section>

      {/* SECTION 1 — WHAT IS IT */}
      <WhatIsIt med={m} />

      {/* SECTION 2 — WHY PATIENTS CHOOSE */}
      <WhyPatientsChoose med={m} />

      {/* SECTION 3 — WHO IS IT FOR */}
      <WhoIsFor items={whoFor} />

      {/* SECTION 4 — EXPECTED RESULTS */}
      <ExpectedResults med={m} />

      {/* SECTION 5 — FAQ */}
      <FAQAccordion items={m.faq || []} title={`${m.name} — Frequently Asked Questions`} />

      {/* SECTION 6 — AVAILABLE STRENGTHS (assessment launch points) */}
      <AvailableStrengths med={m} onPickStrength={onPickStrength} />

      {/* PRICING + BRAND TRUST */}
      <PricingCards slug={slug} packages={packages} />
      <WhyBestRx />
      <HowItWorks4 />
      <NoInsuranceSection />
      <RequestCallbackCTA source={`med_${slug}_mid`} />

      {/* AUTHORITY LINK-BACK (SEO parent-hub structure) */}
      <CategoryLinkback med={m} />

      {/* RELATED PAGES */}
      <RelatedCluster medications={ALL_MEDS} cities={CITIES} currentMed={slug} />

      <Footer />
    </main>
  );
}
